import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { ApolloError } from "@apollo/client"
import { TextField } from "~/fields/text-field"
import { Button } from "~/ui/button"
import { DialogFooter } from "~/ui/dialog"
import { GraphqlError } from "~/ui/errors"
import { Form } from "~/ui/form"

const verificationFormSchema = z.object({
  token: z.string().min(1, { message: "Verification code is required" }),
})

type VerificationFormValues = z.infer<typeof verificationFormSchema>

interface EmailVerificationStepProps {
  email: string
  onVerify: (token: string) => Promise<void>
  onSendAgain: () => void
  onCancel: () => void
  verificationError?: ApolloError
  isVerifying?: boolean
  isSendingAgain?: boolean
  sendAgainText?: string
  description?: string
}

export const EmailVerificationStep: React.FC<EmailVerificationStepProps> = ({
  email,
  onVerify,
  onSendAgain,
  onCancel,
  verificationError,
  isVerifying = false,
  isSendingAgain = false,
  sendAgainText = "Send Again",
  description,
}) => {
  const verificationForm = useForm<VerificationFormValues>({
    resolver: zodResolver(verificationFormSchema),
    defaultValues: {
      token: "",
    },
  })

  const onSubmit = async (values: VerificationFormValues) => {
    await onVerify(values.token)
  }

  const defaultDescription = `A verification code was sent to ${email}. Please check your email and input the code provided.`

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">{description || defaultDescription}</p>
      <Form {...verificationForm}>
        <form onSubmit={verificationForm.handleSubmit(onSubmit)} className="space-y-4">
          <TextField
            control={verificationForm.control}
            name="token"
            label="Code Input"
            placeholder="000000"
            className="text-center tracking-widest"
          />
          {verificationError && <GraphqlError error={verificationError} variant="red-box" />}
        </form>
      </Form>
      <p className="text-sm text-gray-500">
        Didn't receive the code?{" "}
        <button
          type="button"
          onClick={onSendAgain}
          disabled={isSendingAgain}
          className="text-blue-600 underline hover:text-blue-800 disabled:opacity-50"
        >
          {isSendingAgain ? "Sending..." : sendAgainText}
        </button>
      </p>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={verificationForm.handleSubmit(onSubmit)} disabled={isVerifying}>
          {isVerifying ? "Verifying..." : "Verify Code"}
        </Button>
      </DialogFooter>
    </div>
  )
}
